import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiEdit2 } from "react-icons/fi";
import { IoChevronBack } from "react-icons/io5";
import { toast } from "react-toastify";
import { z } from "zod";

import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import {
  getAuthControllerGetCurrentUserQueryKey,
  useAuthControllerGetLoginMethods,
  useAuthControllerUpdateUser,
} from "@/lib/lutely-api/gen/auth/auth";
import type { UserPayloadDto } from "@/lib/lutely-api/gen/dtos";
import { useQueryClient } from "@tanstack/react-query";

import PasswordUpdateForm from "@/components/auth/PasswordUpdateForm";
import UpdateEmailForm from "@/components/auth/UpdateEmailForm";
import PersonaSettingsCard from "@/components/settings/PersonaSettingsCard";
import PlanDetailsCard from "@/components/settings/PlanDetailsCard";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import {
  Box,
  Button,
  Flex,
  Heading,
  Switch,
  Text,
  TextField,
} from "@radix-ui/themes";

type FormValues = {
  firstName: string;
  lastName: string;
};

const searchSchema = z.object({
  returnTo: z.string().optional(),
});

export const Route = createFileRoute("/account/settings/personal")({
  component: RouteComponent,
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Personal Settings | Lutely" }] }),
});

function RouteComponent() {
  const { data: currentUser, isLoading } = useLoggedInUser();
  const { returnTo } = useSearch({ from: "/account/settings/personal" });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
        <p className="text-[14px] text-neutral-ink">Loading...</p>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
        <p className="text-[14px] text-neutral-ink">User not found</p>
      </div>
    );
  }

  return <PersonalInformationForm user={currentUser} returnTo={returnTo} />;
}

type PersonalInformationFormProps = {
  user: UserPayloadDto;
  returnTo?: string;
};

function PersonalInformationForm({
  user,
  returnTo,
}: PersonalInformationFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditingPersonal, setIsEditingPersonal] = useState(!!returnTo);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const { mutate: updateUser } = useAuthControllerUpdateUser();
  const { data: loginMethods = [] } = useAuthControllerGetLoginMethods();
  const isSocial = !loginMethods.includes("email");

  const { register, handleSubmit, reset } = useForm<FormValues>({
    values: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
    },
  });

  const handleEdit = (type: "personal" | "password" | "email") => {
    setIsEditingPersonal(false);
    setIsEditingPassword(false);
    setIsEditingEmail(false);

    if (type === "personal") {
      setIsEditingPersonal(true);
    } else if (type === "password") {
      setIsEditingPassword(true);
    } else if (type === "email") {
      setIsEditingEmail(true);
    }
  };

  const onSavePersonal = (data: FormValues) => {
    updateUser(
      {
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getAuthControllerGetCurrentUserQueryKey(),
          });
          toast.success("Personal information saved successfully!");
          if (returnTo) {
            navigate({ to: returnTo });
          } else {
            setIsEditingPersonal(false);
          }
        },
        onError: (error: any) => {
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  const onCancel = () => {
    reset();
    setIsEditingPersonal(false);
  };

  // The auth/me query cache is the single source of truth for this toggle. We
  // update it optimistically so the switch flips immediately and stays
  // consistent with any background refetch / other-tab update, then roll back
  // if the mutation fails.
  const saleEmailsEnabled = user.saleEmailsEnabled ?? true;
  const { mutate: updateSaleEmails, isPending: isSaleEmailsUpdating } =
    useAuthControllerUpdateUser();

  const onToggleSaleEmails = (enabled: boolean) => {
    if (isSaleEmailsUpdating) return; // guard against concurrent toggles
    const queryKey = getAuthControllerGetCurrentUserQueryKey();
    const previous = queryClient.getQueryData<UserPayloadDto>(queryKey);
    queryClient.setQueryData<UserPayloadDto>(queryKey, (old) =>
      old ? { ...old, saleEmailsEnabled: enabled } : old,
    );
    updateSaleEmails(
      { data: { saleEmailsEnabled: enabled } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey });
          toast.success(
            enabled
              ? "You'll get an email when you make a sale."
              : "You've turned off sale emails.",
          );
        },
        onError: (error: any) => {
          queryClient.setQueryData(queryKey, previous); // roll back
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  // Same optimistic pattern as the sale-emails toggle. Turning it off only
  // changes the account preference — contact already shared on past orders
  // stays shared.
  const shareContactWithSellers = user.shareContactWithSellers ?? false;
  const { mutate: updateShareContact, isPending: isShareContactUpdating } =
    useAuthControllerUpdateUser();

  const onToggleShareContact = (enabled: boolean) => {
    if (isShareContactUpdating) return;
    const queryKey = getAuthControllerGetCurrentUserQueryKey();
    const previous = queryClient.getQueryData<UserPayloadDto>(queryKey);
    queryClient.setQueryData<UserPayloadDto>(queryKey, (old) =>
      old ? { ...old, shareContactWithSellers: enabled } : old,
    );
    updateShareContact(
      { data: { shareContactWithSellers: enabled } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey });
          toast.success(
            enabled
              ? "Artists you buy from can see your name and email."
              : "You'll be asked again at your next checkout.",
          );
        },
        onError: (error: any) => {
          queryClient.setQueryData(queryKey, previous); // roll back
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  return (
    <>
      {/* Mobile Back Button */}
      <div className="md:hidden flex items-center gap-2 mb-6">
        <Link
          to="/account/settings"
          className="flex items-center justify-center w-[34px] h-[34px] bg-white rounded-full shadow-md"
        >
          <IoChevronBack className="text-[20px]" />
        </Link>
        <h1 className="text-[24px] font-semibold">Personal Information</h1>
      </div>

      {/* Account Information Card */}
      <Box className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
        <Flex justify="between" align="start" className="mb-6">
          <Heading as="h2" className="text-[18px] font-semibold">
            Account Information
          </Heading>
          {!isEditingPersonal && (
            <Button
              onClick={() => handleEdit("personal")}
              color="gray"
              size="2"
              radius="full"
              data-testid="account-edit-personal-button"
            >
              <FiEdit2 size={16} aria-hidden="true" />
              Edit
            </Button>
          )}
        </Flex>

        {isEditingPersonal ? (
          <form onSubmit={handleSubmit(onSavePersonal)} className="space-y-4">
            <Text className="text-[12px] text-text-secondary">
              Optional. Your name appears on license agreements and sales
              reports. It will not be displayed on your public profile.
            </Text>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[14px] text-black mb-1">
                  First Name
                </label>
                <TextField.Root
                  {...register("firstName")}
                  className="w-full"
                  data-testid="account-first-name-input"
                />
              </div>
              <div>
                <label className="block text-[14px] text-black mb-1">
                  Last Name
                </label>
                <TextField.Root
                  {...register("lastName")}
                  data-testid="account-last-name-input"
                />
              </div>
            </div>

            <Flex className="flex flex-wrap items-center justify-end gap-[25px] pt-4">
              <Button
                onClick={onCancel}
                size="2"
                color="gray"
                variant="ghost"
                className="text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="2"
                radius="full"
                color="gray"
                data-testid="account-save-personal-button"
              >
                Save
              </Button>
            </Flex>
          </form>
        ) : (
          <Flex direction="column" gap="1">
            <Text className="text-[14px] text-black">Legal Name</Text>
            <Text className="text-[14px] text-neutral-ink">
              {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
            </Text>
          </Flex>
        )}
      </Box>

      {/* Plan Details Card — see PlanDetailsCard for why it also lives here */}
      <PlanDetailsCard />

      {/* Notifications Card */}
      <Box className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
        <Heading as="h2" className="text-[18px] font-semibold mb-4">
          Notifications
        </Heading>
        <Flex justify="between" align="center" gap="4">
          <Box>
            <Text className="text-[14px] text-black">Sale emails</Text>
            <Text as="p" className="text-[12px] text-text-secondary">
              Email me when someone buys my music.
            </Text>
          </Box>
          <Switch
            checked={saleEmailsEnabled}
            onCheckedChange={onToggleSaleEmails}
            disabled={isSaleEmailsUpdating}
            aria-label="Toggle sale emails"
            data-testid="account-sale-emails-toggle"
          />
        </Flex>
      </Box>

      {/* Privacy Card */}
      <Box className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
        <Heading as="h2" className="text-[18px] font-semibold mb-4">
          Privacy
        </Heading>
        <Flex justify="between" align="center" gap="4">
          <Box>
            <Text className="text-[14px] text-black">
              Share contact with artists
            </Text>
            <Text as="p" className="text-[12px] text-text-secondary">
              Let artists you buy from see your name and email. Info already
              shared on past orders isn't removed.
            </Text>
          </Box>
          <Switch
            checked={shareContactWithSellers}
            onCheckedChange={onToggleShareContact}
            disabled={isShareContactUpdating}
            aria-label="Toggle sharing contact with artists"
            data-testid="account-share-contact-toggle"
          />
        </Flex>
      </Box>

      {/* Persona / Role Card */}
      <PersonaSettingsCard user={user} />

      {/* Update Password Card */}
      {!isSocial && (
        <Box className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
          <Flex
            justify="start"
            align="start"
            className="flex-col sm:flex-row sm:justify-between gap-3 sm:gap-0"
          >
            <Box>
              <Heading as="h2" className="text-[18px] font-semibold">
                Update Password
              </Heading>
            </Box>
            {!isEditingPassword && (
              <Button
                onClick={() => handleEdit("password")}
                color="gray"
                size="2"
                radius="full"
              >
                Set Password
              </Button>
            )}
          </Flex>

          {isEditingPassword && (
            <PasswordUpdateForm
              onCancel={() => setIsEditingPassword(false)}
              onSuccess={() => setIsEditingPassword(false)}
            />
          )}
        </Box>
      )}

      {/* Update Email Card */}
      {!isSocial && (
        <Box className="bg-white rounded-lg p-6 mb-6 border border-[rgba(5,12,43,0.06)]">
          <Flex
            justify="start"
            align="start"
            className="flex-col sm:flex-row sm:justify-between gap-3 sm:gap-0"
          >
            <Box>
              <Heading as="h2" className="text-[18px] font-semibold">
                Update Email Address
              </Heading>
              <Text className="text-[14px] text-neutral-ink mt-1">
                {user.email}
              </Text>
            </Box>
            {!isEditingEmail && (
              <Button
                onClick={() => handleEdit("email")}
                color="gray"
                size="2"
                radius="full"
              >
                Update Email
              </Button>
            )}
          </Flex>

          {isEditingEmail && (
            <UpdateEmailForm
              onCancel={() => setIsEditingEmail(false)}
              onSuccess={() => setIsEditingEmail(false)}
            />
          )}
        </Box>
      )}
    </>
  );
}
